/**
 * VoiceCapture — mic acquisition + local Voice Activity Detection.
 *
 * Uses `navigator.mediaDevices.getUserMedia` to grab the mic, wires an
 * `AnalyserNode` to compute RMS at ~30 Hz, and fires `onVoiceActivity`
 * when the speaking state flips (with a configurable hangover window
 * to prevent flicker).
 *
 * iOS Safari gotcha: `getUserMedia` only works in response to a user
 * gesture. Call `VoiceCapture.acquire()` from a click handler.
 */

import type { VoiceChatConfig } from './VoiceChatConfig.js';

export interface VoiceActivityEvent {
  isSpeaking: boolean;
  /** Most recent RMS level in dBFS. */
  dbfs: number;
}

export class VoiceCapture {
  readonly config: VoiceChatConfig;
  readonly ctx: AudioContext;

  private _stream: MediaStream | null = null;
  private _analyser: AnalyserNode | null = null;
  private _source: MediaStreamAudioSourceNode | null = null;
  private _muteGain: GainNode | null = null;
  private _vadBuf: Float32Array | null = null;
  private _vadInterval: ReturnType<typeof setInterval> | null = null;
  private _isSpeaking = false;
  private _lastActiveAt = -Infinity;

  onVoiceActivity: ((ev: VoiceActivityEvent) => void) | null = null;

  constructor(ctx: AudioContext, config: VoiceChatConfig) {
    this.ctx = ctx;
    this.config = config;
  }

  /**
   * Acquire the mic. Must be called from a user gesture on iOS Safari.
   * Returns the MediaStream for passing to `VoicePeerConnection` constructors.
   */
  async acquire(): Promise<MediaStream> {
    // A second acquire (device change, retry after a denied prompt) used to
    // overwrite the nodes and leak the old VAD interval + keep the old mic hot.
    if (this._stream) this.dispose();
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: this.config.echoCancellation,
        noiseSuppression: this.config.noiseSuppression,
        autoGainControl: this.config.autoGainControl,
      },
    });
    this._stream = stream;
    // Build the VAD graph: source → mute-gain → analyser.
    // The `_muteGain` lives between source and analyser so mute / PTT-off
    // gates the local VAD too (you don't want your VAD firing while muted).
    this._source = this.ctx.createMediaStreamSource(stream);
    this._muteGain = this.ctx.createGain();
    this._muteGain.gain.value = this.config.mode === 'alwaysOn' ? 1 : 0;
    this._analyser = this.ctx.createAnalyser();
    this._analyser.fftSize = 512;
    this._source.connect(this._muteGain).connect(this._analyser);
    this._vadBuf = new Float32Array(this._analyser.fftSize);
    this._vadInterval = setInterval(() => this._tickVad(), 33); // ~30 Hz
    // The send tracks come back enabled: a push-to-talk session streamed the
    // mic to every peer until the first PTT press. Apply the initial state.
    this.setMute(this.config.mode !== 'alwaysOn');
    return stream;
  }

  /** Set mute state — 0 = silent / muted, 1 = open. */
  setMute(muted: boolean): void {
    if (!this._stream) return;
    for (const track of this._stream.getAudioTracks()) track.enabled = !muted;
    if (this._muteGain) this._muteGain.gain.value = muted ? 0 : 1;
  }

  /** For PTT: call with true on press, false on release. */
  setPressToTalk(pressed: boolean): void {
    this.setMute(!pressed);
  }

  /** True if mic is currently producing above-threshold audio. */
  get isSpeaking(): boolean { return this._isSpeaking; }

  /** Get the current raw MediaStream (for passing to PeerConnections). */
  get stream(): MediaStream | null { return this._stream; }

  /** Release the mic + audio graph. */
  dispose(): void {
    if (this._vadInterval !== null) {
      clearInterval(this._vadInterval);
      this._vadInterval = null;
    }
    try { this._source?.disconnect(); } catch { /* ignore */ }
    try { this._muteGain?.disconnect(); } catch { /* ignore */ }
    try { this._analyser?.disconnect(); } catch { /* ignore */ }
    if (this._stream) {
      for (const track of this._stream.getTracks()) track.stop();
      this._stream = null;
    }
  }

  private _tickVad(): void {
    const analyser = this._analyser;
    const buf = this._vadBuf;
    if (!analyser || !buf) return;
    analyser.getFloatTimeDomainData(buf as Float32Array<ArrayBuffer>);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    const dbfs = 20 * Math.log10(rms + 1e-10);
    const now = performance.now();
    const over = dbfs > this.config.vadThresholdDb;
    if (over) this._lastActiveAt = now;
    const stillInHangover = (now - this._lastActiveAt) < this.config.vadHangoverMs;
    const newSpeaking = over || stillInHangover;
    if (newSpeaking !== this._isSpeaking) {
      this._isSpeaking = newSpeaking;
      this.onVoiceActivity?.({ isSpeaking: newSpeaking, dbfs });
    }
  }
}
